import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Plus, Trash2, Loader2, Upload, Palette, X, ImagePlus, Type, FileType
} from "lucide-react";

// Common web-safe fonts + popular Google Fonts
const PRESET_FONTS = [
  { name: "Arial", family: "Arial, sans-serif" },
  { name: "Helvetica", family: "Helvetica, sans-serif" },
  { name: "Georgia", family: "Georgia, serif" },
  { name: "Times New Roman", family: "'Times New Roman', serif" },
  { name: "Verdana", family: "Verdana, sans-serif" },
  { name: "Roboto", family: "'Roboto', sans-serif", google: true },
  { name: "Open Sans", family: "'Open Sans', sans-serif", google: true },
  { name: "Lato", family: "'Lato', sans-serif", google: true },
  { name: "Montserrat", family: "'Montserrat', sans-serif", google: true },
  { name: "Poppins", family: "'Poppins', sans-serif", google: true },
  { name: "Playfair Display", family: "'Playfair Display', serif", google: true },
  { name: "Oswald", family: "'Oswald', sans-serif", google: true },
];

export default function CompanyAssetsPage() {
  const { token } = useAuth();
  const [assets, setAssets] = useState([]);
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [colorDialogOpen, setColorDialogOpen] = useState(false);
  const [fontDialogOpen, setFontDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState(null);
  
  // Form states
  const [colors, setColors] = useState([]);
  const [fonts, setFonts] = useState([]);
  const [newColorName, setNewColorName] = useState("");
  const [newColorHex, setNewColorHex] = useState("#3B82F6");
  const [selectedFont, setSelectedFont] = useState("");
  
  // Font upload states
  const [fontFile, setFontFile] = useState(null);
  const [fontFileName, setFontFileName] = useState("");
  const [uploadingFont, setUploadingFont] = useState(false);
  const fontFileInputRef = useRef(null);
  
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadName, setUploadName] = useState("");
  const [uploadType, setUploadType] = useState("LOGO");
  const [uploading, setUploading] = useState(false);
  const [savingColors, setSavingColors] = useState(false);
  const [savingFonts, setSavingFonts] = useState(false);
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchAssets();
    fetchBranding();
  }, []);

  const fetchAssets = async () => {
    try {
      const response = await axios.get(`${API}/company-assets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssets(response.data);
    } catch (error) {
      console.error("Failed to fetch company assets", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBranding = async () => {
    try {
      const response = await axios.get(`${API}/company-branding`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranding(response.data);
      setColors(response.data.colors || []);
      setFonts(response.data.fonts || []);
    } catch (error) {
      console.error("Failed to fetch company branding", error);
    }
  };

  // Color handlers
  const addColor = () => {
    if (!newColorName.trim()) {
      toast.error("Please enter a color name");
      return;
    }
    if (colors.some(c => c.hex_code === newColorHex)) {
      toast.error("This color already exists");
      return;
    }
    setColors([...colors, { name: newColorName, hex_code: newColorHex }]);
    setNewColorName("");
    setNewColorHex("#3B82F6");
  };

  const removeColor = (index) => {
    setColors(colors.filter((_, i) => i !== index));
  };

  const saveColors = async () => {
    setSavingColors(true);
    try {
      await axios.put(`${API}/company-branding`, { colors }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Brand colors saved");
      setColorDialogOpen(false);
      fetchBranding();
    } catch (error) {
      toast.error("Failed to save colors");
    } finally {
      setSavingColors(false);
    }
  };

  // Font handlers
  const addPresetFont = () => {
    if (!selectedFont) {
      toast.error("Please select a font");
      return;
    }
    const font = PRESET_FONTS.find(f => f.name === selectedFont);
    if (!font) return;
    if (fonts.some(f => f.name === font.name)) {
      toast.error("This font is already added");
      return;
    }
    setFonts([...fonts, { name: font.name, family: font.family, google: font.google || false }]);
    setSelectedFont("");
  };

  const handleFontFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const validExtensions = ['.ttf', '.otf', '.woff', '.woff2'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!validExtensions.includes(ext)) {
      toast.error("Please select a valid font file (.ttf, .otf, .woff, .woff2)");
      return;
    }
    
    setFontFile(file);
    // Generate font name from filename
    const nameFromFile = file.name.replace(/\.[^/.]+$/, "").replace(/[-_]/g, ' ');
    setFontFileName(nameFromFile);
  };

  const uploadFontFile = async () => {
    if (!fontFile || !fontFileName.trim()) {
      toast.error("Please select a font file and enter a name");
      return;
    }
    
    setUploadingFont(true);
    try {
      const formData = new FormData();
      formData.append('file', fontFile);
      formData.append('name', fontFileName);
      
      const response = await axios.post(`${API}/company-branding/upload-font`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(`Font "${fontFileName}" uploaded successfully`);
      setFontFile(null);
      setFontFileName("");
      fetchBranding();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload font");
    } finally {
      setUploadingFont(false);
    }
  };

  const removeFont = (index) => {
    setFonts(fonts.filter((_, i) => i !== index));
  };

  const saveFonts = async () => {
    setSavingFonts(true);
    try {
      await axios.put(`${API}/company-branding`, { fonts }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Brand fonts saved");
      setFontDialogOpen(false);
      fetchBranding();
    } catch (error) {
      toast.error("Failed to save fonts");
    } finally {
      setSavingFonts(false);
    }
  };

  // Asset handlers
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }
    
    setUploadFile(file);
    setUploadName(file.name.replace(/\.[^/.]+$/, ""));
    setUploadPreview(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadName.trim()) {
      toast.error("Please select a file and enter a name");
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('name', uploadName);
      formData.append('asset_type', uploadType);
      
      await axios.post(`${API}/company-assets/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success("Asset uploaded successfully");
      resetUploadForm();
      fetchAssets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAsset = async () => {
    if (!assetToDelete) return;
    
    try {
      await axios.delete(`${API}/company-assets/${assetToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Asset deleted");
      setDeleteDialogOpen(false);
      setAssetToDelete(null);
      fetchAssets();
    } catch (error) {
      toast.error("Failed to delete asset");
    }
  };

  const resetUploadForm = () => {
    setUploadDialogOpen(false);
    setUploadFile(null);
    setUploadPreview(null);
    setUploadName("");
    setUploadType("LOGO");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-black tracking-tight">Company Assets</h1>
        <p className="text-muted-foreground mt-1">
          Your brand identity and asset library, used across your creatives
        </p>
      </div>

      {/* Branding Sections */}
      <div className="grid gap-4">
        
        {/* Themes and Colours */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-gradient-to-br from-pink-500/10 to-purple-500/10">
                  <Palette className="h-6 w-6 text-purple-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Themes & Colours</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Add your specific company colours to use in your ad creatives
                  </p>
                  {/* Display saved colors */}
                  {branding?.colors?.length > 0 && (
                    <div className="flex gap-2 mt-3">
                      {branding.colors.map((c, i) => (
                        <div key={i} className="flex items-center gap-1.5" title={`${c.name}: ${c.hex_code}`}>
                          <div 
                            className="w-6 h-6 rounded-full border-2 border-white shadow-sm"
                            style={{ backgroundColor: c.hex_code }}
                          />
                          <span className="text-xs text-muted-foreground">{c.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <Button variant="outline" onClick={() => setColorDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                {branding?.colors?.length > 0 ? 'Edit' : 'Add'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Fonts */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-gradient-to-br from-blue-500/10 to-cyan-500/10">
                  <Type className="h-6 w-6 text-blue-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Fonts</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Add your specific company fonts to use in your ad creatives
                  </p>
                  {/* Display saved fonts */}
                  {branding?.fonts?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {branding.fonts.map((f, i) => (
                        <Badge key={i} variant="secondary" className="font-normal">
                          <span style={{ fontFamily: f.family }}>{f.name}</span>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <Button variant="outline" onClick={() => setFontDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                {branding?.fonts?.length > 0 ? 'Edit' : 'Add'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Assets */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-gradient-to-br from-orange-500/10 to-amber-500/10">
                  <ImagePlus className="h-6 w-6 text-orange-500" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">Assets</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Add your company logos, icons and images to use in your ad creatives
                  </p>
                  {/* Display saved assets */}
                  {assets.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {assets.map((asset) => (
                        <div 
                          key={asset.id} 
                          className="relative group w-12 h-12 rounded-lg border bg-secondary/30 p-1 overflow-hidden"
                          title={asset.name}
                        >
                          <img 
                            src={asset.file_url} 
                            alt={asset.name}
                            className="w-full h-full object-contain"
                          />
                          <button
                            onClick={() => { setAssetToDelete(asset); setDeleteDialogOpen(true); }}
                            className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                          >
                            <Trash2 className="h-4 w-4 text-white" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <Button onClick={() => setUploadDialogOpen(true)}>
                <Upload className="h-4 w-4 mr-2" />
                Upload
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Colors Dialog */}
      <Dialog open={colorDialogOpen} onOpenChange={setColorDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5 text-purple-500" />
              Brand Colours
            </DialogTitle>
            <DialogDescription>
              Add your company colours for quick access when creating ads
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {colors.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No colours saved yet</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {colors.map((color, index) => (
                  <div key={index} className="flex items-center gap-3 p-2 border rounded-lg">
                    <div 
                      className="w-10 h-10 rounded-lg border shadow-inner"
                      style={{ backgroundColor: color.hex_code }}
                    />
                    <div className="flex-1">
                      <p className="font-medium text-sm">{color.name}</p>
                      <p className="text-xs text-muted-foreground">{color.hex_code}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeColor(index)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="border-t pt-4">
              <Label className="text-sm mb-2 block">Add New Colour</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Colour name (e.g., Primary)"
                  value={newColorName}
                  onChange={(e) => setNewColorName(e.target.value)}
                  className="flex-1"
                />
                <Input
                  type="color"
                  value={newColorHex}
                  onChange={(e) => setNewColorHex(e.target.value)}
                  className="w-14 h-10 p-1 cursor-pointer"
                />
                <Button variant="outline" onClick={addColor}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <Button className="w-full" onClick={saveColors} disabled={savingColors}>
              {savingColors ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save Colours
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fonts Dialog */}
      <Dialog open={fontDialogOpen} onOpenChange={setFontDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Type className="h-5 w-5 text-blue-500" />
              Brand Fonts
            </DialogTitle>
            <DialogDescription>
              Add fonts for use in your ad creatives
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {fonts.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No fonts saved yet</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {fonts.map((font, index) => (
                  <div key={index} className="flex items-center gap-3 p-2 border rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium" style={{ fontFamily: font.family }}>{font.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {font.uploaded ? 'Uploaded' : font.google ? 'Google Font' : font.custom ? 'Custom' : 'System Font'}
                      </p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeFont(index)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="border-t pt-4 space-y-3">
              <div>
                <Label className="text-sm mb-2 block">Add from Library</Label>
                <div className="flex gap-2">
                  <select
                    className="flex-1 h-10 border rounded-md px-3 text-sm"
                    value={selectedFont}
                    onChange={(e) => setSelectedFont(e.target.value)}
                  >
                    <option value="">Select a font...</option>
                    {PRESET_FONTS.filter(f => !fonts.some(saved => saved.name === f.name)).map(font => (
                      <option key={font.name} value={font.name} style={{ fontFamily: font.family }}>
                        {font.name} {font.google ? '(Google)' : ''}
                      </option>
                    ))}
                  </select>
                  <Button variant="outline" onClick={addPresetFont}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div>
                <Label className="text-sm mb-2 block">Or Upload Font File</Label>
                <input
                  type="file"
                  ref={fontFileInputRef}
                  accept=".ttf,.otf,.woff,.woff2"
                  className="hidden"
                  onChange={handleFontFileSelect}
                />
                <div className="space-y-2">
                  {fontFile ? (
                    <div className="flex items-center gap-2 p-2 border rounded-lg bg-secondary/30">
                      <FileType className="h-5 w-5 text-blue-500" />
                      <div className="flex-1">
                        <p className="text-sm font-medium">{fontFile.name}</p>
                        <p className="text-xs text-muted-foreground">{(fontFile.size / 1024).toFixed(1)} KB</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => { setFontFile(null); setFontFileName(""); }}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div 
                      className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-blue-500/50 transition-colors"
                      onClick={() => fontFileInputRef.current?.click()}
                    >
                      <Upload className="h-6 w-6 text-muted-foreground mx-auto mb-1" />
                      <p className="text-xs text-muted-foreground">Click to select .ttf, .otf, .woff, or .woff2</p>
                    </div>
                  )}
                  {fontFile && (
                    <div className="flex gap-2">
                      <Input
                        placeholder="Font display name"
                        value={fontFileName}
                        onChange={(e) => setFontFileName(e.target.value)}
                        className="flex-1"
                      />
                      <Button onClick={uploadFontFile} disabled={uploadingFont || !fontFileName.trim()}>
                        {uploadingFont ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <Button className="w-full" onClick={saveFonts} disabled={savingFonts}>
              {savingFonts ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save Fonts
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Asset Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={(open) => { if (!open) resetUploadForm(); else setUploadDialogOpen(open); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-orange-500" />
              Upload Asset
            </DialogTitle>
            <DialogDescription>
              Upload logos, icons, or images to use in your creatives
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              className="hidden"
              onChange={handleFileSelect}
            />
            
            {uploadPreview ? (
              <div className="relative rounded-lg overflow-hidden border bg-secondary/30 aspect-square max-w-[200px] mx-auto">
                <img src={uploadPreview} alt="Preview" className="w-full h-full object-contain p-2" />
                <Button
                  variant="destructive"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => { setUploadFile(null); setUploadPreview(null); }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div 
                className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <ImagePlus className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Click to select an image</p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Asset Name</Label>
              <Input
                value={uploadName}
                onChange={(e) => setUploadName(e.target.value)}
                placeholder="e.g., Company Logo"
              />
            </div>

            <div className="space-y-2">
              <Label>Asset Type</Label>
              <select 
                className="w-full h-10 border rounded-md px-3"
                value={uploadType}
                onChange={(e) => setUploadType(e.target.value)}
              >
                <option value="LOGO">Logo</option>
                <option value="ICON">Icon</option>
                <option value="IMAGE">Image</option>
              </select>
            </div>

            <Button 
              className="w-full" 
              onClick={handleUpload} 
              disabled={!uploadFile || !uploadName.trim() || uploading}
            >
              {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              Upload Asset
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Asset Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Asset?</DialogTitle>
            <DialogDescription>
              This will permanently delete "{assetToDelete?.name}".
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteAsset}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
